import { Component } from 'preact';
import PropTypes from "prop-types";
import { combineClasses, get } from "@green24/npm-javascript-utils";
import ButtonsConstructor from "./Input/Button/ButtonsConstructor";
import Interpret from "./Localization/Interpret";
import BoxTerminal from "./BoxTerminal";
import { E_Session_PaymentState } from "../../models/constants/Enums_Session";
import Fa from "./Fa";
import { withLocalizationContext } from "../components";
import { AppUtils } from "../../utils/AppUtils";
import { sessionServices } from "../../services/session-services";
import MessageModal from "./Modal/MessageModal";
import { ErrorUtils } from "../../utils/ErrorUtils";
import { ComponentUtils, ValueUpdateListeners } from "@green24/npm-preact-utils";
import { E_Payment_Mode } from "../../models/constants/Enums_Payment";
import { E_Route } from "../../models/constants/Enum_Routes";
import { route } from "preact-router";

class PaymentTerminal extends Component {
	constructor(props) {
		super(props);

		this.state = {
			sending: false,
			cancelling: false,
			hasPaid: false,
			session: props.session,
		}

		this._valueUpdateListener = new ValueUpdateListeners(this);
		this._destinationListenerRemover = null;
	}

	componentDidMount() {
		this._mounted = true;

		const {sessionID, session, onSession} = this.props;
		if(sessionID && !session) {
			sessionServices.getItem(sessionID).then(session => {
				if(this._mounted) {
					onSession(session);

					this.setState({session});
				}
			});
		}

		this._valueUpdateListener.reflectPropToState("session");
	}

	componentDidUpdate(previousProps, previousState, snapshot) {
		this._valueUpdateListener.componentDidUpdate(previousProps, previousState);
	}

	componentWillUnmount() {
		this._mounted = false;

		const {sending, sessionID} = this.state;

		if(sending && sessionID > 0) {
			this._cancelPayment();
			this._destinationListenerRemover && this._destinationListenerRemover();
			window.greenSocket.unsubscribeTopic(sessionServices.getPaymentSocketUrl(sessionID), "Terminal component unmount");
		}
	}

	_renderHasPaidContent() {
		return (
			<div className={"payment-text"}>
				<Fa icon={"check"} style={{fontSize: "2.2em"}}/>
				<p><Interpret id={"payment.success"}/></p>
			</div>
		);
	}

	_renderSendingContent() {
		return (
			<>
				<div className={"payment-icons"}>
					<Fa icon={"credit-card-front"}/>
					<Fa icon={"signal-stream"}/>
				</div>
				<p className={"payment-text"}><Interpret id={"payment.instructions"}/></p>
			</>
		);
	}

	_renderContent(hasPaid) {
		const {idleContentSlot, hasPaidSlot, sendingSlot} = this.props;
		const {sending} = this.state;

		if(hasPaid) {
			return ComponentUtils.resolvePolymorphProp(
				hasPaidSlot,
				{
					function: f => f(() => this._renderHasPaidContent()),
					undefined: () => this._renderHasPaidContent(),
				},
				() => hasPaidSlot,
			);
		}

		if(sending) {
			return ComponentUtils.resolvePolymorphProp(
				sendingSlot,
				{
					function: f => f(() => this._renderSendingContent()),
					undefined: () => this._renderSendingContent(),
				},
				() => sendingSlot,
			);
		}

		return idleContentSlot;
	}

	render(
		{className, style, title, contentHeaderLeftSlot, contentHeaderSlot, contentSlot, physicalReference, contentRightSlot},
		{sending, cancelling, session, hasPaid}
	) {
		return (
			<section className={combineClasses("payment-terminal", className)} style={style}>
				<BoxTerminal
					title={title}
					contentHeaderLeftSlot={
						<div className={"service"}>
							<div>{get(session, "service.serviceType.titles", '‎', titles => <Interpret id={titles}/>)}</div>
							<div>{get(session, "service.physicalReference", physicalReference)}</div>
						</div>
					}
					contentHeaderSlot={contentHeaderSlot}
					contentLeftSlot={
						sending &&
						<ButtonsConstructor buttons={[
							{
								className: "cancel-button",
								text: "cancel",
								icon: "credit-card",
								action: () => this._cancelPayment(),
								disabled: cancelling,
								spinner: cancelling,
							}
						]}/>
					}
					contentSlot={
						ComponentUtils.resolvePolymorphProp(
							contentSlot,
							{
								function: f => f({
									sending,
									cancelling,
									hasPaid,
								}),
								undefined: () => this._renderContent(hasPaid),
							},
							() => this._renderContent(hasPaid),
						)
					}
					contentRightSlot={
						<div>
							{contentRightSlot}

							{
								!hasPaid &&
								<ButtonsConstructor buttons={[
									{
										className: "pay-button",
										text: "pay",
										icon: "credit-card",
										action: () => this._startPayment(),
										disabled: cancelling || sending,
										spinner: sending,
									}
								]}/>
							}
						</div>
					}
				/>
			</section>
		);
	}

	_startPayment() {
		const {localizationContext, sessionID, onSession, onPaid, paymentMode} = this.props;
		const simulatePayment = AppUtils.simulatePaymentProcess;
		const sessionSocketUrl = sessionServices.getPaymentSocketUrl(sessionID);

		this.setState({sending: true});

		if(simulatePayment) {
			sessionServices.simulateStartPayment(simulatePayment).then(session => {
				let newSessionData = {
					...session,
					paymentState: E_Session_PaymentState.SUCCESS,
				};
				onSession(newSessionData);

				this.setState({sending: false});

				this._showToastOnSuccess();
				onPaid(newSessionData);
			});
		}
		else {
			this._destinationListenerRemover = window.greenSocket.onDestination(sessionSocketUrl, (session) => {
				const __unsubscribe = (reason) => {
					this.setState({sending: false});
					window.greenSocket.unsubscribeTopic(sessionSocketUrl, reason);
					this._destinationListenerRemover();
				};
				const __onCancelled = () => {
					__unsubscribe("Cancelled");

					window.modal.open(
						<MessageModal
							icon={"stop-circle"}
							title={"payment.cancelled"}
							actionButtons={() => [
								{
									text: "ok",
									action: () => {
										route(E_Route.BASE);
										window.modal.close(true);
									},
								},
							]}
						/>
					);
				};

				session = {
					...this.state.session,
					...session.data,
				};

				onSession(session);

				switch (session.paymentState) {
					case E_Session_PaymentState.SUCCESS:
						this.setState({hasPaid: true});
						__unsubscribe("Payment success");

						this._showToastOnSuccess();

						onPaid(session);
						break;
					case E_Session_PaymentState.ERROR:
						if(this.state.cancelling) {
							__onCancelled();

							return;
						}

						__unsubscribe("Payment error");

						window.modal.open(
							<MessageModal
								icon={"exclamation-triangle"}
								title={"payment.error.title"}
								message={session.paymentResponseText}
								actionButtons={[
									{
										className: "confirm",
										text: "retry",
										action: () => {
											window.modal.close();

											this._startPayment();
										}
									}
								]}
							/>,
							{
								accent: "error",
							}
						);
						break;
					case null:
						__onCancelled();
						break;
				}
			});

			window.greenSocket.subscribeTopic(sessionSocketUrl);

			sessionServices.startPayment(sessionID, localizationContext.getActiveLanguageISOCode(), paymentMode, simulatePayment).catch((res) => {
				if(this._mounted) {
					this.setState({sending: false});

					//TBD: More specific error?
					let error = ErrorUtils.getErrorFromResponse(res);
					window.modal.open(
						<MessageModal
							icon={error.icon}
							title={error.title}
							titleTranslated={error.titleTranslated}
							message={"payment.start.error.message"}
						/>,
						{
							accent: "error",
						}
					);
				}
			});
		}
	}

	_cancelPayment() {
		const {sessionID} = this.props;

		if(this._mounted) {
			this.setState({cancelling: true});

			sessionServices.cancelPayment(sessionID).catch((res) => {
				this._mounted && this.setState({cancelling: false});
				//TBD: More specific error?
				let error = ErrorUtils.getErrorFromResponse(res);
				window.modal.open(
					<MessageModal
						icon={error.icon}
						title={error.title}
						titleTranslated={error.titleTranslated}
						message={"payment.cancel.error.message"}
					/>,
					{
						accent: "error",
					}
				);
			});
		}
		else {
			sessionServices.cancelPayment(sessionID);
		}
	}

	_showToastOnSuccess() {
		const {showToastOnPaymentSuccess} = this.props;

		if(showToastOnPaymentSuccess) {
			window.toaster.showToast({
				icon: "check",
				title: "payment.success",
				lifespan: "5s",
				accent: "success",
			});
		}
	}

	static get propTypes() {
		return {
			className: PropTypes.string,
			style: PropTypes.object,
			children: PropTypes.any,

			title: PropTypes.any.isRequired,
			contentHeaderLeftSlot: PropTypes.any,
			contentSlot: PropTypes.any,
			hasPaidSlot: PropTypes.any,
			sendingSlot: PropTypes.any,
			idleContentSlot: PropTypes.any,

			sessionID: PropTypes.number.isRequired,
			session: PropTypes.object,
			physicalReference: PropTypes.string,
			paymentMode: PropTypes.oneOf(Object.values(E_Payment_Mode)).isRequired,
			showToastOnPaymentSuccess: PropTypes.bool,

			onSession: PropTypes.func,
			onPaid: PropTypes.func,

			//withLocalizationContext
			localizationContext: PropTypes.object,
		}
	}

	static get stateTypes() {
		return {
			sending: PropTypes.bool,
			cancelling: PropTypes.bool,
			session: PropTypes.object,
		}
	}

	static get defaultProps() {
		return {
			title: "payment.title",
			onSession: session => null,
			onPaid: session => null,
			idleContentSlot: <div/>,
			paymentMode: E_Payment_Mode.INITIAL_PAYMENT,
			showToastOnPaymentSuccess: true,
		}
	}
}

export default withLocalizationContext(PaymentTerminal);
